import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../../FormLegacy/FormFieldsV2/TextAreaField'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { TextArea } from '@/components/ui/text-area'
import { TextInput } from '@/components/ui/text-input'
import { ZoomFrame } from '@/components/ui/zoomable-image'
import { TIME_BLOCK_REASON_CREATE_OPTIONS } from '@/pages/reservations/time-blocks/time-blocks.constants'

import { getAssetLink } from '../../../../helpers/get-asset-link'
import { buildTimeBlockSummaryText } from '../../helpers'

const TimeBlockSummary = ({
  onBack = () => {},
  handleFieldChange = () => {},
  setAddNotes = () => {},
  onSubmit = () => {},
  addNotes,
  formState,
  canGoNext,
  title,
  timezone,
  selectedLocation,
}) => {
  const { t } = useTranslation(['reservations', 'common'])
  return (
    <div>
      <div className="reservations-modal-content reservations-add-time-block-modal">
        <span className="title">{title}</span>
        <div className="summary-item">
          <ZoomFrame
            className="summary-item__image-frame"
            src={getAssetLink({
              path: 'chitown-building',
              file: selectedLocation?.image || formState.amenity.image,
            })}
            title={selectedLocation?.name || formState.amenity.name}
          >
            <img
              src={getAssetLink({
                path: 'chitown-building',
                file: selectedLocation?.image || formState.amenity.image,
              })}
              alt=""
            />
          </ZoomFrame>
          <div className="summary-item__wrapper">
            <span className="summary-item__wrapper__title">
              {selectedLocation?.name || formState.amenity.name}
            </span>
            <span className="summary-item__wrapper__subtitle">
              {t('reservations:reservationSummary.dateAndTime')}
            </span>
            <span className="summary-item__wrapper__text">
              {buildTimeBlockSummaryText(
                {
                  amenity: formState.amenity,
                  date: formState.date,
                  endDate: formState.endDate,
                  startTime: formState.startTime,
                  endTime: formState.endTime,
                  blockFullDay: formState.blockFullDay,
                  blockMultipleDays: formState.blockMultipleDays,
                  timezone,
                },
                t,
              )}
            </span>
          </div>
        </div>
        <div className="reservations-modal-input-row" style={{ marginBottom: 16 }}>
          <Select
            required
            label="Time block reason"
            name="reason"
            options={TIME_BLOCK_REASON_CREATE_OPTIONS}
            value={formState.reason}
            onChange={(value) => handleFieldChange({ target: { name: 'reason', value } })}
          />
        </div>
        {formState.reason === 'Other' && (
          <div className="reservations-modal-input-row" style={{ marginBottom: 16 }}>
            <TextInput
              required
              characterLimit={25}
              label="Custom reason"
              maxLength={25}
              name="reasonOther"
              placeholder="e.g. Elevator inspection"
              value={formState.reasonOther || ''}
              onChange={handleFieldChange}
            />
          </div>
        )}
        <div className="reservations-modal-input-row" style={{ marginBottom: 16 }}>
          <TextArea
            characterLimit={200}
            label="Description"
            maxLength={200}
            name="description"
            placeholder="Anything staff should know about this time block"
            value={formState.description || ''}
            onChange={handleFieldChange}
          />
        </div>
        <div className="reservations-modal-input-row" style={{ marginBottom: 16 }}>
          <div className="input-container inline">
            <CheckboxField
              name="addNotes"
              value={addNotes}
              onChange={() => setAddNotes(!addNotes)}
              label={t('reservations:timeBlockSummaryStep.addTimeBlockNote')}
            />
          </div>
        </div>
        {addNotes && (
          <React.Fragment>
            <div className="reservations-modal-input-row" style={{ marginBottom: 16 }}>
              <div className="input-container">
                <div className="input-label">{t('reservations:reservationNoteForm.noteName')}</div>
                <TextField
                  name="noteTitle"
                  onChange={handleFieldChange}
                  value={formState.noteTitle || ''}
                  placeholder={t('reservations:reservationNoteForm.enterNoteNamePlaceholder')}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className="reservations-modal-input-row">
              <div className="input-container">
                <div className="input-label">
                  {t('reservations:reservationNoteForm.noteDescription')}
                </div>
                <TextAreaField
                  name="noteBody"
                  maxLength="260"
                  onChange={handleFieldChange}
                  value={formState.noteBody || ''}
                  placeholder={t('reservations:reservationNoteForm.notesPlaceholder')}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
      <div className="reservations-modal-actions">
        <Button use="secondary" onClick={onBack}>
          {t('common:actions.back')}
        </Button>
        <Button use="primary" disabled={!canGoNext} onClick={onSubmit}>
          {t('reservations:timeBlockSummaryStep.createTimeBlock')}
        </Button>
      </div>
    </div>
  )
}

TimeBlockSummary.propTypes = {
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  handleFieldChange: PropTypes.func,
  setAddNotes: PropTypes.func,
  onSubmit: PropTypes.func,
  canGoNext: PropTypes.bool,
  addNotes: PropTypes.bool,
  title: PropTypes.string,
  formState: PropTypes.object,
  timezone: PropTypes.string,
  selectedLocation: PropTypes.object,
}

export default TimeBlockSummary
